import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  ShieldCheck,
  RefreshCw,
  Trash2,
  Award,
  Edit2,
  Copy,
  SlidersHorizontal,
} from "lucide-react";
import {
  listChannels,
  createChannel,
  syncMetaTemplates,
  deleteChannel,
  type CurrentUser,
  type ChannelAccount,
} from "../../lib/api";

export function ChannelsView({ token, user }: { token: string; user: CurrentUser }) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<"all" | "meta_official" | "qr_whatsapp">("meta_official");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState<ChannelAccount | null>(null);

  // Form State
  const [channelName, setChannelName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [wabaId, setWabaId] = useState("");
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const channelsQuery = useQuery({
    queryKey: ["channels", activeTab, user.tenant_id],
    queryFn: () => listChannels(token, activeTab === "all" ? undefined : activeTab, user.tenant_id),
    refetchInterval: 10_000,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createChannel(
        token,
        {
          type: "meta_official",
          name: channelName.trim(),
          phone_number: phoneNumber.trim(),
          waba_id: wabaId.trim(),
          phone_number_id: phoneNumberId.trim(),
          access_token: accessToken.trim(),
        },
        user.tenant_id,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["channels"] });
      closeModal();
    },
  });

  const syncTemplatesMutation = useMutation({
    mutationFn: (channelId: string) => syncMetaTemplates(token, channelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns", "templates"] });
      alert("Meta approved templates synced successfully!");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (channelId: string) => deleteChannel(token, channelId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["channels"] }),
  });

  function openCreate() {
    setEditingChannel(null);
    setChannelName("");
    setPhoneNumber("");
    setWabaId("");
    setPhoneNumberId("");
    setAccessToken("");
    setIsModalOpen(true);
  }

  function openEdit(channel: ChannelAccount) {
    setEditingChannel(channel);
    setChannelName(channel.name);
    setPhoneNumber(channel.phone_number || "");
    setWabaId(channel.waba_id || "");
    setPhoneNumberId(channel.phone_number_id || "");
    setAccessToken("");
    setIsModalOpen(true);
  }

  function closeModal() {
    setIsModalOpen(false);
    setEditingChannel(null);
  }

  function copyText(text: string) {
    navigator.clipboard.writeText(text);
  }

  const channels = channelsQuery.data || [];

  const filteredChannels = channels.filter((c) =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.phone_number || "").includes(searchQuery) ||
    (c.waba_id || "").includes(searchQuery)
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-blue-600" />
            Meta Verified Official Channels
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Manage your Meta WhatsApp Business Accounts (WABA), Green Tick Verified numbers, and Cloud API tokens.
          </p>
        </div>

        <button
          onClick={openCreate}
          className="btn-primary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>Add Meta Verified WABA</span>
        </button>
      </div>

      {/* Filter Toolbar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Search Box */}
          <div className="relative w-72">
            <input
              type="text"
              placeholder="Search by name, phone, WABA ID..."
              className="modern-input w-full text-xs py-2"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <button
            onClick={() => setActiveTab("meta_official")}
            className={`filter-chip ${activeTab === "meta_official" ? "border-blue-500 text-blue-700 bg-blue-50" : ""}`}
          >
            <ShieldCheck className="w-3.5 h-3.5 text-blue-600" />
            <span>Official Meta WABA ({channels.filter(c => c.type === "meta_official").length})</span>
          </button>

          <button
            onClick={() => setActiveTab("all")}
            className={`filter-chip ${activeTab === "all" ? "border-slate-400 text-slate-800" : ""}`}
          >
            <span>All Channels ({channels.length})</span>
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => channelsQuery.refetch()}
            className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs"
            title="Refresh database state"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${channelsQuery.isFetching ? "animate-spin" : ""}`} />
            <span>Sync</span>
          </button>

          <button className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs">
            <SlidersHorizontal className="w-3.5 h-3.5 text-slate-500" />
            <span>View</span>
          </button>
        </div>
      </div>

      {/* Channels Grid / Table */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {channelsQuery.isLoading ? (
          <div className="col-span-3 p-12 text-center text-slate-400 text-xs bg-white border border-slate-200 rounded-xl">
            Loading Meta Verified channels...
          </div>
        ) : filteredChannels.length === 0 ? (
          <div className="col-span-3 p-12 text-center bg-white border border-slate-200 rounded-xl shadow-2xs space-y-3">
            <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mx-auto text-blue-600">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <h3 className="text-sm font-bold text-slate-900">No Meta Verified Channels configured yet</h3>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              Connect your Official WhatsApp Cloud API account using your Meta WABA ID, Phone Number ID, and System User Access Token.
            </p>
            <button onClick={openCreate} className="btn-primary text-xs py-2 px-3.5 mx-auto inline-flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" />
              <span>Connect Meta Account</span>
            </button>
          </div>
        ) : (
          filteredChannels.map((channel) => (
            <div
              key={channel.id}
                className="bg-white border border-slate-200 rounded-xl p-5 shadow-2xs flex flex-col justify-between hover:border-slate-300 transition-all space-y-4"
              >
                <div>
                  {/* Card Top Title & Status */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-xl bg-blue-50 text-blue-600 border border-blue-200">
                        <ShieldCheck className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                          {channel.name}
                          <span title="Meta Verified WABA">
                            <Award className="w-4 h-4 text-blue-600" />
                          </span>
                        </h3>
                        <p className="text-xs font-mono font-semibold text-slate-700 mt-0.5">
                          {channel.phone_number || channel.identifier}
                        </p>
                      </div>
                    </div>

                    <span
                      className={`status-pill ${
                        channel.status === "active" ? "status-pill--connected" : "status-pill--disconnected"
                      }`}
                    >
                      {channel.status}
                    </span>
                  </div>

                  {/* Channel Metadata Box */}
                  <div className="mt-4 p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-2 text-xs">
                    <div className="flex justify-between items-center text-slate-500">
                      <span>Quality Rating:</span>
                      <span className="font-bold text-emerald-600 flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-emerald-500" />
                        {channel.quality_score || "GREEN (High)"}
                      </span>
                    </div>

                    <div className="flex justify-between items-center text-slate-500">
                      <span>Messaging Tier:</span>
                      <span className="font-mono text-slate-800 font-bold">{channel.messaging_tier || "TIER_10K"}</span>
                    </div>

                    <div className="flex justify-between items-center text-slate-500">
                      <span>WABA ID:</span>
                      <div className="flex items-center gap-1 font-mono text-slate-700">
                        <span>{channel.waba_id || "10987654321"}</span>
                        <button onClick={() => copyText(channel.waba_id || "")} className="hover:text-slate-900">
                          <Copy className="w-3 h-3 text-slate-400" />
                        </button>
                      </div>
                    </div>

                    <div className="flex justify-between items-center text-slate-500">
                      <span>Phone Number ID:</span>
                      <div className="flex items-center gap-1 font-mono text-slate-700">
                        <span>{channel.phone_number_id || "20987654321"}</span>
                        <button onClick={() => copyText(channel.phone_number_id || "")} className="hover:text-slate-900">
                          <Copy className="w-3 h-3 text-slate-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card Actions */}
                <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                  <button
                    className="btn-secondary text-xs py-1.5 px-2.5 flex items-center gap-1.5 flex-1 shadow-2xs"
                    onClick={() => syncTemplatesMutation.mutate(channel.id)}
                    disabled={syncTemplatesMutation.isPending}
                    title="Pull pre-approved templates from Meta Graph API"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 text-blue-600 ${syncTemplatesMutation.isPending ? "animate-spin" : ""}`} />
                    <span>Sync Templates</span>
                  </button>

                  <button
                    onClick={() => openEdit(channel)}
                    className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 hover:text-slate-900 transition-colors"
                    title="Edit Credentials"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>

                  <button
                    className="p-1.5 rounded-lg border border-rose-200 hover:bg-rose-50 text-rose-500 hover:text-rose-700 transition-colors"
                    onClick={() => {
                      if (confirm(`Delete Meta Channel "${channel.name}"?`)) {
                        deleteMutation.mutate(channel.id);
                      }
                    }}
                    title="Delete Channel"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))
        )}
      </div>

      {/* Add / Edit Meta Verified Channel Modal */}
      {isModalOpen && (
        <div className="modal-backdrop" onClick={closeModal}>
          <div className="modal-card max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-4">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-blue-600" />
                <h3 className="text-base font-bold text-slate-900">
                  {editingChannel ? `Edit Meta Channel: ${editingChannel.name}` : "Connect Meta Verified Official WABA"}
                </h3>
              </div>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-700 text-xs font-bold">
                ✕
              </button>
            </div>

            <div className="space-y-3.5">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Channel Display Name *</label>
                <input
                  type="text"
                  placeholder="e.g. Official Support Desk"
                  className="modern-input w-full text-xs"
                  value={channelName}
                  onChange={(e) => setChannelName(e.target.value)}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">WhatsApp Phone Number *</label>
                <input
                  type="text"
                  placeholder="+919323818985"
                  className="modern-input w-full text-xs font-mono"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">WhatsApp Business Account (WABA) ID *</label>
                  <input
                    type="text"
                    placeholder="e.g. 109876543210987"
                    className="modern-input w-full text-xs font-mono"
                    value={wabaId}
                    onChange={(e) => setWabaId(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Phone Number ID *</label>
                  <input
                    type="text"
                    placeholder="e.g. 209876543210987"
                    className="modern-input w-full text-xs font-mono"
                    value={phoneNumberId}
                    onChange={(e) => setPhoneNumberId(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Meta Permanent System User Access Token *
                </label>
                <input
                  type="password"
                  placeholder="EAAG... (never expires token)"
                  className="modern-input w-full text-xs font-mono"
                  value={accessToken}
                  onChange={(e) => setAccessToken(e.target.value)}
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Generated in Meta Business Manager &gt; System Users &gt; Generate Token with `whatsapp_business_messaging` permissions.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button className="btn-secondary text-xs" onClick={closeModal}>
                  Cancel
                </button>
                <button
                  className="btn-primary text-xs"
                  onClick={() => createMutation.mutate()}
                  disabled={!channelName.trim() || !phoneNumber.trim() || !wabaId.trim() || createMutation.isPending}
                >
                  {createMutation.isPending ? "Connecting..." : editingChannel ? "Save Changes" : "Save & Verify WABA"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
